import React, { useState, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import OrganisationSettingsForm from '../components/organisation/OrganisationSettingsForm';
import PowerPointSettingsForm from '../components/organisation/PowerPointSettingsForm';
import WhitelabelSettingsForm from '../components/organisation/WhitelabelSettingsForm';
import organisationSettingsService, { OrganisationSettings, PowerPointSettings } from '../services/organisationSettingsService';
import { useTheme } from '../contexts/ThemeContext';
import './OrganisationManagementPage.css';

const OrganisationManagementPage: React.FC = () => {
  const { theme } = useTheme();
  const [notification, setNotification] = useState<string | null>(null);
  const [notificationType, setNotificationType] = useState<'success' | 'error'>('success');
  const [organisationSettings, setOrganisationSettings] = useState<OrganisationSettings | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch organization settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const settings = await organisationSettingsService.getOrganisationSettings();
        setOrganisationSettings(settings);
      } catch (error: any) {
        console.error('Error fetching organization settings:', error);
        setNotification('Failed to load organization settings');
        setNotificationType('error');
        setTimeout(() => setNotification(null), 5000);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSettingsUpdate = (settings: OrganisationSettings) => {
    setOrganisationSettings(settings); // Update local state
    setNotification('Organisation settings updated successfully!');
    setNotificationType('success');
    setTimeout(() => setNotification(null), 5000);
  };

  const handlePowerPointSettingsUpdate = (settings: PowerPointSettings) => {
    // Update the PowerPoint settings in the local state
    if (organisationSettings) {
      setOrganisationSettings({
        ...organisationSettings,
        powerPointSettings: settings
      });
    }
    setNotification('PowerPoint settings updated successfully!');
    setNotificationType('success');
    setTimeout(() => setNotification(null), 5000);
  };

  const handleWhitelabelSettingsUpdate = (settings: OrganisationSettings) => {
    setOrganisationSettings(settings);
    setNotification('Whitelabel settings updated successfully!');
    setNotificationType('success');
    setTimeout(() => setNotification(null), 5000);
  };

  const handleError = (error: string) => {
    setNotification(error);
    setNotificationType('error');
    setTimeout(() => setNotification(null), 5000);
  };

  return (
    <div className="dashboard-page" data-theme={theme}>
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-header">
          <h1>Organisation Management</h1>
          <p className="dashboard-subtitle">
            Manage organisation settings, structure, and configuration
          </p>
        </div>

        <div className="content-container">
          <div className="organisation-management-content">
            {notification && (
              <div className={`notification ${notificationType}`}>
                {notification}
                <button 
                  className="notification-close"
                  onClick={() => setNotification(null)}
                >
                  ×
                </button>
              </div>
            )}

            <div className="management-section">
              <div className="section-header">
                <h2>Organisation Settings</h2>
                <p>Configure validation thresholds and other organisation-specific settings.</p>
              </div>
              
              <OrganisationSettingsForm 
                onUpdate={handleSettingsUpdate}
                onError={handleError}
              />
            </div>

            <div className="management-section">
              <div className="section-header">
                <h2>Whitelabel</h2>
                <p>Customize product branding with your own logos and product name.</p>
              </div>
              
              {loading ? (
                <div className="loading-text">Loading settings...</div>
              ) : (
                <WhitelabelSettingsForm 
                  currentSettings={organisationSettings}
                  onUpdate={handleWhitelabelSettingsUpdate}
                  onError={handleError}
                />
              )}
            </div>

            <div className="management-section">
              <div className="section-header">
                <h2>PowerPoint Settings</h2>
                <p>Customize typography and backgrounds for your PowerPoint presentations.</p>
              </div>
              
              {loading ? (
                <div className="loading-text">Loading settings...</div>
              ) : (
                <PowerPointSettingsForm 
                  currentSettings={organisationSettings?.powerPointSettings}
                  onUpdate={handlePowerPointSettingsUpdate}
                  onError={handleError}
                />
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganisationManagementPage;
